import React, { useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { useFocusTrap } from "../../../../hook/useFocusTrap";
import { REGISTRATION_KEYBOARD_SCOPE_ATTR } from "../../../RegistrationLayout/registrationFocusStyles";
import {
  formatSavedCardBrandLabel,
  formatSavedCardExpiryLabel,
} from "../RegistrationCardOnFile/RegistrationCardOnFile.validator";
import {
  ModalOverlay,
  ModalPanel,
  ModalHeader,
  ModalHeaderText,
  ModalTitle,
  ModalCloseBtn,
  ModalBody,
  GotItBtn,
  ConfirmScrollArea,
  ConfirmIntro,
  DetailList,
  DetailRow,
  DetailLabel,
  DetailValue,
  ConfirmActions,
  CancelBtn,
} from "../../Insurance/InsuranceDetailsConfirmModal/InsuranceDetailsConfirmModal.style";

const LABEL_FALLBACKS = {
  NUTRITION_CARD_CONFIRM_LBL_TITLE: "Confirm payment method",
  NUTRITION_CARD_CONFIRM_DESC:
    "Please review your card on file details before continuing.",
  NUTRITION_CARD_CONFIRM_BTN_CONFIRM: "Confirm & continue",
  NUTRITION_CARD_CONFIRM_BTN_EDIT: "Edit details",
  NUTRITION_CARD_LBL_PAYMENT_METHOD: "Payment Method",
  NUTRITION_CARD_PREVIEW_LBL_CARDHOLDER: "Cardholder",
  NUTRITION_CARD_LBL_EXPIRY: "Expiration date",
  NUTRITION_CARD_LBL_ZIP: "Billing ZIP code",
  ADDCARD_PLACEHOLDER_CARD_NUMBER: "Card number",
  NUTRITION_CARD_PREVIEW_LBL_BRAND: "Card",
};

const translateWithFallback = (t, key) => {
  const translated = t(key);
  if (translated !== key) {
    return translated;
  }
  return LABEL_FALLBACKS[key] || translated;
};

export const buildCardConfirmRows = (details = {}, t) => {
  const safeDetails = details && typeof details === "object" ? details : {};
  const brandLabel = formatSavedCardBrandLabel(safeDetails.brand, t);
  const expiryLabel = formatSavedCardExpiryLabel(
    safeDetails.expMonth,
    safeDetails.expYear
  );
  const maskedNumber = safeDetails.last4
    ? `•••• •••• •••• ${safeDetails.last4}`
    : "—";

  const rows = [
    {
      key: "brand",
      label: translateWithFallback(t, "NUTRITION_CARD_LBL_PAYMENT_METHOD"),
      value: brandLabel || "—",
    },
    {
      key: "cardNumber",
      label: translateWithFallback(t, "ADDCARD_PLACEHOLDER_CARD_NUMBER"),
      value: maskedNumber,
    },
    {
      key: "expiry",
      label: translateWithFallback(t, "NUTRITION_CARD_LBL_EXPIRY"),
      value: expiryLabel || "—",
    },
    {
      key: "cardholderName",
      label: translateWithFallback(t, "NUTRITION_CARD_PREVIEW_LBL_CARDHOLDER"),
      value: safeDetails.cardholderName || "—",
    },
  ];

  if (safeDetails.billingZipCode) {
    rows.push({
      key: "billingZipCode",
      label: translateWithFallback(t, "NUTRITION_CARD_LBL_ZIP"),
      value: safeDetails.billingZipCode,
    });
  }

  return rows;
};

const CardDetailsConfirmModal = ({
  isOpen,
  details,
  confirmLabel,
  onConfirm,
  onClose,
  isConfirming = false,
}) => {
  const { t } = useTranslation();
  const confirmButtonRef = useRef(null);
  const panelRef = useRef(null);

  useFocusTrap({
    isOpen: Boolean(isOpen && details),
    containerRef: panelRef,
    initialFocusRef: confirmButtonRef,
    onEscape: isConfirming ? undefined : onClose,
  });

  useEffect(() => {
    if (!isOpen || !details) {
      return undefined;
    }

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [isOpen, details]);

  if (!isOpen || !details || typeof document === "undefined") {
    return null;
  }

  const rows = buildCardConfirmRows(details, t);
  const title = translateWithFallback(t, "NUTRITION_CARD_CONFIRM_LBL_TITLE");
  const confirmText =
    confirmLabel ||
    translateWithFallback(t, "NUTRITION_CARD_CONFIRM_BTN_CONFIRM");

  return createPortal(
    <ModalOverlay
      {...REGISTRATION_KEYBOARD_SCOPE_ATTR}
      role="presentation"
      onClick={(event) => {
        if (event.target === event.currentTarget && !isConfirming) {
          onClose?.();
        }
      }}
    >
      <ModalPanel
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="card-details-confirm-title"
      >
        <ModalHeader>
          <ModalHeaderText>
            <ModalTitle id="card-details-confirm-title">{title}</ModalTitle>
            <ConfirmIntro>
              {translateWithFallback(t, "NUTRITION_CARD_CONFIRM_DESC")}
            </ConfirmIntro>
          </ModalHeaderText>
          <ModalCloseBtn
            type="button"
            aria-label={t("NUTRITION_COMMON_ARIA_CLOSE", {
              defaultValue: "Close",
            })}
            onClick={onClose}
            disabled={isConfirming}
          >
            ×
          </ModalCloseBtn>
        </ModalHeader>
        <ModalBody>
          <ConfirmScrollArea>
            <DetailList>
              {rows.map((row) => (
                <DetailRow key={row.key}>
                  <DetailLabel>{row.label}</DetailLabel>
                  <DetailValue>{row.value}</DetailValue>
                </DetailRow>
              ))}
            </DetailList>
          </ConfirmScrollArea>
          <ConfirmActions>
            <CancelBtn
              type="button"
              onClick={onClose}
              disabled={isConfirming}
            >
              {translateWithFallback(t, "NUTRITION_CARD_CONFIRM_BTN_EDIT")}
            </CancelBtn>
            <GotItBtn
              ref={confirmButtonRef}
              type="button"
              onClick={onConfirm}
              disabled={isConfirming}
              aria-busy={isConfirming}
            >
              {confirmText}
            </GotItBtn>
          </ConfirmActions>
        </ModalBody>
      </ModalPanel>
    </ModalOverlay>,
    document.body
  );
};

export default CardDetailsConfirmModal;
